import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
// Native MUI Components
import { 
  Card, 
  CardContent, 
  CardHeader,
  Typography, 
  Button,
  Box,
  Stack,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider
} from '@mui/material';
import { Receipt, Add } from '@mui/icons-material';
import { Trip, Traveller, Apartment, Bill, BillData } from '@/types';
import { billService } from '@/services/billService';
import { listenToTripById } from '@/services/tripService';
import { travellerService } from '@/services/travellerService';
import { apartmentService } from '@/services/apartmentService';
import * as billCalculationService from '@/services/billCalculationService';
import BillCards from './BillCards';
import BillCard from './BillCard';
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/contexts/AuthContext';

const EnhancedBillOverview: React.FC = () => {
  const { tripId } = useParams<{ tripId: string }>();
  const { t } = useI18n();
  const { currentUser } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [trip, setTrip] = useState<Trip | null>(null);
  const [travellers, setTravellers] = useState<Traveller[]>([]);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [bills, setBills] = useState<Bill[]>([]);
  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);
  const [selectedApartmentId, setSelectedApartmentId] = useState<string | null>(null);
  const [selectedRecipientId, setSelectedRecipientId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tripId) return;

    const unsubscribeTrip = listenToTripById(tripId, (tripData) => setTrip(tripData || null));
    const unsubscribeTravellers = travellerService.listenToTravellers(tripId, setTravellers);
    const unsubscribeApartments = apartmentService.listenToApartments(tripId, setApartments);
    const unsubscribeBills = billService.listenToBills(tripId, setBills);

    return () => {
      unsubscribeTrip();
      unsubscribeTravellers();
      unsubscribeApartments();
      unsubscribeBills();
    };
  }, [tripId]);

  const handleGenerateBill = async () => {
    if (!tripId || !selectedApartmentId || !trip) {
      setError(t('bill.missingData'));
      return;
    }
    
    if (!currentUser) {
      setError(t('bill.mustBeLoggedIn'));
      return;
    }

    if (!selectedRecipientId) {
      setError(t('bill.persistent.selectRecipient'));
      return;
    }

    try {
      setIsLoading(true);
      await billService.generateBill(
        tripId,
        selectedApartmentId,
        trip,
        apartments,
        travellers,
        currentUser.uid,
        currentUser.displayName,
        selectedRecipientId
      );
      setIsGenerateDialogOpen(false);
      setSelectedApartmentId(null);
      setSelectedRecipientId(null);
    } catch (error) {
      console.error('Error generating bill:', error);
      setError(t('bill.persistent.billGenerationError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateBillStatus = (apartmentId: string, status: 'not_sent' | 'sent' | 'paid') => {
    if (!tripId) return;
    apartmentService.updateApartment(tripId, apartmentId, { billStatus: status });
  };

  const handleUpdatePaymentStatus = (apartmentId: string, isPaid: boolean) => {
    if (!tripId) return;
    apartmentService.updateApartment(tripId, apartmentId, { isPaid });
  };

  const refreshData = () => {
    // Data will refresh automatically due to listeners
  };

  if (!trip) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '200px',
        gap: 2 
      }}>
        <CircularProgress />
        <Typography color="text.secondary">{t('common.loading')}</Typography>
      </Box>
    );
  }

  const billData = billCalculationService.getBillDataForTrip(trip, apartments, travellers);
  const apartmentsWithBills = bills.map(bill => bill.apartmentId);
  const apartmentsWithoutBills = billData.filter(
    bill => !apartmentsWithBills.includes(bill.apartmentId) && bill.travellers.length > 0
  );

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab 
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {t('bill.persistent.preview')}
              <Chip 
                label={apartmentsWithoutBills.length} 
                size="small" 
                color="info"
              />
            </Box>
          } 
        />
        <Tab 
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {t('bill.persistent.title')}
              <Chip 
                label={bills.length} 
                size="small" 
                color="success"
              />
            </Box>
          } 
        />
      </Tabs>

      {tabValue === 0 && (
        <Stack spacing={3}>
          <Card>
            <CardHeader
              title={t('bill.persistent.preview')}
              subheader={t('bill.persistent.noPersistentBillsDescription')}
              action={
                apartmentsWithoutBills.length > 0 && (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setIsGenerateDialogOpen(true)}
                    sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      px: { xs: 1, sm: 2 },
                      py: { xs: 0.5, sm: 1 },
                      minHeight: { xs: 32, sm: 36 }
                    }}
                  >
                    {t('bill.persistent.generatePersistentBill')}
                  </Button>
                )
              }
            />
          </Card>

          {apartmentsWithoutBills.length > 0 ? (
            <BillCards 
              billData={apartmentsWithoutBills} 
            />
          ) : (
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  {bills.length > 0 
                    ? t('bill.persistent.allApartmentsHaveBills')
                    : t('bill.persistent.noPersistentBills')
                  }
                </Typography>
              </CardContent>
            </Card>
          )}
        </Stack>
      )}

      {tabValue === 1 && (
        <Stack spacing={3}>
          {bills.length > 0 ? (
            <Grid container spacing={3}>
              {bills.map((bill) => (
                <Grid item xs={12} md={6} lg={4} key={bill.id}>
                  <BillCard
                    bill={bill}
                    trip={trip}
                    onUpdate={refreshData}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  {t('bill.persistent.noPersistentBills')}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Stack>
      )}

      {/* Generate Bill Dialog */}
      <Dialog 
        open={isGenerateDialogOpen} 
        onClose={() => setIsGenerateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={false}
        PaperProps={{
          sx: {
            m: { xs: 1, sm: 3 }, // Reduce margin on mobile
            maxHeight: { xs: '90vh', sm: '80vh' } // Ensure dialog fits on mobile
          }
        }}
      >
        <DialogTitle>{t('bill.persistent.confirmGenerate')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('bill.persistent.confirmGenerateMessage')}
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend">{t('bill.persistent.selectApartment')}</FormLabel>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                {apartmentsWithoutBills.map((bill) => (
                  <Card 
                    key={bill.apartmentId}
                    variant={selectedApartmentId === bill.apartmentId ? "outlined" : "elevation"}
                    sx={{ 
                      cursor: 'pointer',
                      border: selectedApartmentId === bill.apartmentId ? 2 : 0,
                      borderColor: 'primary.main'
                    }}
                    onClick={() => {
                      setSelectedApartmentId(bill.apartmentId);
                      setSelectedRecipientId(null); // Reset recipient when apartment changes
                    }}
                  >
                    <CardContent sx={{ py: 1 }}>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 1, sm: 0 }
                      }}>
                        <Typography variant="subtitle2">
                          {bill.apartmentName}
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          €{bill.grandTotal.toFixed(2)}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {bill.travellers.length} {bill.travellers.length === 1 ? 'Reisender' : 'Reisende'}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </FormControl>

            {selectedApartmentId && (
              <>
                <Divider />
                <FormControl component="fieldset">
                  <FormLabel component="legend">{t('bill.persistent.selectRecipient')}</FormLabel>
                  <RadioGroup
                    value={selectedRecipientId || ''}
                    onChange={(e) => setSelectedRecipientId(e.target.value)}
                  >
                    {travellers
                      .filter(t => t.apartmentId === selectedApartmentId)
                      .map((traveller) => (
                        <FormControlLabel
                          key={traveller.id}
                          value={traveller.id}
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="body2">{traveller.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {traveller.address?.street}, {traveller.address?.postalCode} {traveller.address?.city}
                              </Typography>
                            </Box>
                          }
                        />
                      ))}
                  </RadioGroup>
                </FormControl>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: { xs: 2, sm: 3 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 }
        }}>
          <Button 
            onClick={() => setIsGenerateDialogOpen(false)}
            fullWidth={true}
            sx={{ order: { xs: 2, sm: 1 } }}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleGenerateBill}
            variant="contained"
            disabled={!selectedApartmentId || !selectedRecipientId || isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : <Receipt />}
            fullWidth={true}
            sx={{ order: { xs: 1, sm: 2 } }}
          >
            {t('bill.generateBill')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EnhancedBillOverview;
